import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Bike as BikeIcon, Activity, Wrench, Shield, Gauge, TrendingUp } from 'lucide-react';
import { Bike } from '@/services/types';

interface BikeStatsCardProps {
  bikes: Bike[];
  totalCount: number;
  loading?: boolean;
}

interface AnimatedCounterProps {
  value: number;
  duration?: number;
  format?: (value: number) => string;
}

const AnimatedCounter: React.FC<AnimatedCounterProps> = ({ 
  value, 
  duration = 1000, 
  format = (v) => v.toString() 
}) => {
  const [displayValue, setDisplayValue] = React.useState(0);

  React.useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setDisplayValue(Math.floor(easeOutQuart * value));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [value, duration]);

  return <span>{format(displayValue)}</span>;
};

export const BikeStatsCard: React.FC<BikeStatsCardProps> = ({ 
  bikes, 
  totalCount, 
  loading = false 
}) => {
  const stats = React.useMemo(() => {
    const availableBikes = bikes.filter(b => b.status === 'available').length;
    const inUseBikes = bikes.filter(b => b.status === 'in_use').length;
    const maintenanceBikes = bikes.filter(b => b.status === 'maintenance').length;
    
    const blockedBikes = bikes.filter(b => b.movement_blocked === true).length;
    const speedLimitedBikes = bikes.filter(b => 
      b.speed_limit !== null && b.speed_limit !== undefined && !b.movement_blocked
    ).length;
    const normalBikes = bikes.filter(b => 
      !b.movement_blocked && (b.speed_limit === null || b.speed_limit === undefined)
    ).length;
    
    // Правильная обработка mileage как строки
    const totalMileage = bikes.reduce((sum, bike) => {
      const mileageValue = typeof bike.mileage === 'string' 
        ? parseFloat(bike.mileage) || 0 
        : 0;
      return sum + mileageValue;
    }, 0);
    const avgMileage = bikes.length > 0 ? totalMileage / bikes.length : 0;
    const utilization = totalCount > 0 ? (inUseBikes / totalCount) * 100 : 0;

    return {
      availableBikes,
      inUseBikes,
      maintenanceBikes,
      blockedBikes,
      speedLimitedBikes,
      normalBikes,
      totalMileage,
      avgMileage,
      utilization
    };
  }, [bikes, totalCount]);

  const formatNumber = (num: number) => 
    new Intl.NumberFormat('ru-RU').format(num);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BikeIcon className="h-5 w-5" />
            Статистика велосипедов
          </CardTitle>
          <CardDescription>Загрузка статистики...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 bg-muted animate-pulse rounded" />
                <div className="h-6 bg-muted animate-pulse rounded" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BikeIcon className="h-5 w-5" />
          Статистика велосипедов
        </CardTitle>
        <CardDescription>
          Общая информация по {formatNumber(totalCount)} велосипедам
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {/* Доступные велосипеды */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <BikeIcon className="h-4 w-4" />
              Доступные
            </div>
            <div className="text-2xl font-bold text-green-600">
              <AnimatedCounter value={stats.availableBikes} />
            </div>
            <div className="text-xs text-muted-foreground">
              готовы к аренде
            </div>
          </div>

          {/* В аренде */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Activity className="h-4 w-4" />
              В аренде
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-blue-600">
                <AnimatedCounter value={stats.inUseBikes} />
              </span>
              <Badge variant="outline" className="text-blue-600 border-blue-200">
                <AnimatedCounter 
                  value={stats.utilization} 
                  format={(v) => `${v.toFixed(1)}%`}
                />
              </Badge>
            </div>
            <div className="text-xs text-muted-foreground">
              использование флота
            </div>
          </div>

          {/* На обслуживании */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Wrench className="h-4 w-4" />
              Обслуживание
            </div>
            <div className="text-2xl font-bold text-orange-600">
              <AnimatedCounter value={stats.maintenanceBikes} />
            </div>
            {stats.blockedBikes > 0 && (
              <div className="flex items-center gap-1 text-red-600 text-xs">
                <Shield className="h-3 w-3" />
                <AnimatedCounter value={stats.blockedBikes} /> заблокировано
              </div>
            )}
          </div>

          {/* Средний пробег */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <TrendingUp className="h-4 w-4" />
              Средний пробег
            </div>
            <div className="text-2xl font-bold text-purple-600">
              <AnimatedCounter 
                value={stats.avgMileage} 
                format={(v) => `${formatNumber(v)} км`}
                duration={1200}
              />
            </div>
            {stats.speedLimitedBikes > 0 && (
              <div className="flex items-center gap-1 text-amber-600 text-xs">
                <Gauge className="h-3 w-3" />
                <AnimatedCounter value={stats.speedLimitedBikes} /> ограничено
              </div>
            )}
          </div>
        </div>

        {/* Дополнительная статистика по управлению движением */}
        {(stats.blockedBikes > 0 || stats.speedLimitedBikes > 0) && (
          <div className="mt-4 pt-4 border-t">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Заблокированы:</span>
                <span className="font-medium text-red-600">
                  <AnimatedCounter value={stats.blockedBikes} />
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Ограничена скорость:</span>
                <span className="font-medium text-amber-600">
                  <AnimatedCounter value={stats.speedLimitedBikes} />
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Без ограничений:</span>
                <span className="font-medium text-green-600">
                  <AnimatedCounter value={stats.normalBikes} />
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Общий пробег флота */}
        {stats.totalMileage > 0 && (
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Общий пробег флота:</span>
              <span className="font-medium">
                <AnimatedCounter 
                  value={stats.totalMileage} 
                  format={(v) => `${formatNumber(v)} км`}
                />
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
